/**
 * Color Palette
 *
 * Centralized color system for the application.
 * All colors follow a consistent shade scale from 50 (lightest) to 950 (darkest).
 *
 * Usage:
 * ```typescript
 * import { colors } from '@/styles/colors'
 *
 * // Use in components
 * <div style={{ backgroundColor: colors.blue[500] }} />
 *
 * // Semantic colors
 * colors.success[600]  // → green-600
 * colors.error[100]    // → red-100
 * ```
 *
 * Note: These colors are also available as Tailwind utilities:
 * - `bg-blue-500`, `text-blue-500`, `border-blue-500`, etc.
 */

export type ColorShade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950
export type ColorScale = Record<ColorShade, string>

// Core Colors - Full scale
const gray: ColorScale = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
  950: '#030712',
}

const blue: ColorScale = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#4299E1', // Primary blue from nodeColors
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
  950: '#172554',
}

const green: ColorScale = {
  50: '#F0FDF4',
  100: '#DCFCE7',
  200: '#BBF7D0',
  300: '#86EFAC',
  400: '#4ADE80',
  500: '#48BB78', // From nodeColors
  600: '#16A34A',
  700: '#15803D',
  800: '#166534',
  900: '#14532D',
  950: '#052E16',
}

const red: ColorScale = {
  50: '#FEF2F2',
  100: '#FEE2E2',
  200: '#FECACA',
  300: '#FCA5A5',
  400: '#F87171',
  500: '#F56565', // From nodeColors
  600: '#DC2626',
  700: '#B91C1C',
  800: '#991B1B',
  900: '#7F1D1D',
  950: '#450A0A',
}

const yellow: ColorScale = {
  50: '#FEFCE8',
  100: '#FEF9C3',
  200: '#FEF08A',
  300: '#ECC94B', // From nodeColors
  400: '#FACC15',
  500: '#EAB308',
  600: '#CA8A04',
  700: '#A16207',
  800: '#854D0E',
  900: '#713F12',
  950: '#422006',
}

const orange: ColorScale = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316', // From tailwind.config
  600: '#EA580C',
  700: '#C2410C',
  800: '#9A3412',
  900: '#ED8936', // From nodeColors (alternative orange)
  950: '#431407',
}

const purple: ColorScale = {
  50: '#FAF5FF',
  100: '#F3E8FF',
  200: '#E9D5FF',
  300: '#D8B4FE',
  400: '#9F7AEA', // From nodeColors
  500: '#A855F7',
  600: '#9333EA',
  700: '#7E22CE',
  800: '#6B21A8',
  900: '#581C87',
  950: '#3B0764',
}

const pink: ColorScale = {
  50: '#FDF2F8',
  100: '#FCE7F3',
  200: '#FBCFE8',
  300: '#F9A8D4',
  400: '#F472B6',
  500: '#ED64A6', // From nodeColors
  600: '#DB2777',
  700: '#BE185D',
  800: '#9D174D',
  900: '#831843',
  950: '#500724',
}

const teal: ColorScale = {
  50: '#F0FDFA',
  100: '#CCFBF1',
  200: '#99F6E4',
  300: '#5EEAD4',
  400: '#2DD4BF',
  500: '#38B2AC', // From nodeColors
  600: '#0D9488',
  700: '#0F766E',
  800: '#115E59',
  900: '#134E4A',
  950: '#042F2E',
}

const amber: ColorScale = {
  50: '#FFFBEB',
  100: '#FEF3C7',
  200: '#FDE68A',
  300: '#FCD34D',
  400: '#FBBF24',
  500: '#F59E0B',
  600: '#D97706',
  700: '#B45309',
  800: '#92400E',
  900: '#78350F',
  950: '#451A03',
}

const violet: ColorScale = {
  50: '#F5F3FF',
  100: '#EDE9FE',
  200: '#DDD6FE',
  300: '#C4B5FD',
  400: '#A78BFA',
  500: '#8B5CF6',
  600: '#7C3AED',
  700: '#6D28D9',
  800: '#5B21B6',
  900: '#4C1D95',
  950: '#2E1065',
}

const indigo: ColorScale = {
  50: '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  500: '#6366F1',
  600: '#4F46E5',
  700: '#4338CA',
  800: '#3730A3',
  900: '#312E81',
  950: '#1E1B4B',
}

const cyan: ColorScale = {
  50: '#ECFEFF',
  100: '#CFFAFE',
  200: '#A5F3FC',
  300: '#67E8F9',
  400: '#22D3EE',
  500: '#06B6D4',
  600: '#0891B2',
  700: '#0E7490',
  800: '#155E75',
  900: '#164E63',
  950: '#083344',
}

// Export individual color scales
export const colorScales = {
  gray,
  blue,
  green,
  red,
  yellow,
  orange,
  purple,
  pink,
  teal,
  amber,
  violet,
  indigo,
  cyan,
}

// Export semantic color mappings
export const semanticColors = {
  success: green,
  error: red,
  warning: amber,
  info: blue,
  processing: yellow,
}

// Main colors object with both scales and semantic names
export const colors = {
  // Color scales
  gray,
  blue,
  green,
  red,
  yellow,
  orange,
  purple,
  pink,
  teal,
  amber,
  violet,
  indigo,
  cyan,

  // Semantic aliases
  success: green,
  error: red,
  warning: amber,
  info: blue,
  processing: yellow,
}

// Type for accessing colors
export type ColorName = keyof typeof colors
export type Color = typeof colors[ColorName]

// Helper function to get color with shade
export const getColor = (colorName: ColorName, shade: ColorShade = 500): string => {
  return colors[colorName][shade]
}

// Export default
export default colors
